/**
 * CIDR / source-range helpers for the Apprise VM network rules (pure, no deps).
 * IPv4 uses the helpers in cidr.ts; IPv6 is only checked
 * structurally (valid address + prefix) because the wizard never does maths on it.
 */
import { isAlignedCidr, parseCidr, type ParsedCidr } from './cidr'

export type ParsedAnyCidr =
  | { family: 4; prefix: number; cidr: ParsedCidr }
  | { family: 6; prefix: number }

/** "*", "Internet", "Any" — never an acceptable admin / admin-port source. */
export function isWildcardSource(value: string): boolean {
  return ['*', 'internet', 'any'].includes(value.trim().toLowerCase())
}

function isValidIpv6(address: string): boolean {
  if (!/^[0-9a-fA-F:]+$/.test(address) || !address.includes(':')) return false
  const doubleColon = address.split('::')
  if (doubleColon.length > 2) return false
  const groups = (part: string) => (part === '' ? [] : part.split(':'))
  const head = groups(doubleColon[0])
  const tail = doubleColon.length === 2 ? groups(doubleColon[1]) : []
  const all = [...head, ...tail]
  if (all.some((g) => !/^[0-9a-fA-F]{1,4}$/.test(g))) return false
  return doubleColon.length === 2 ? all.length <= 7 : all.length === 8
}

/** Parse an IPv4 or IPv6 CIDR (must carry a prefix length). Returns null when invalid. */
export function parseAnyCidr(value: string): ParsedAnyCidr | null {
  const v = value.trim()
  if (!v.includes('/')) return null
  if (v.includes(':')) {
    const [addr, pfx] = v.split('/')
    if (!/^\d{1,3}$/.test(pfx ?? '') || !isValidIpv6(addr)) return null
    const prefix = Number(pfx)
    return prefix >= 0 && prefix <= 128 ? { family: 6, prefix } : null
  }
  const cidr = parseCidr(v)
  return cidr ? { family: 4, prefix: cidr.prefix, cidr } : null
}

export function isOpenToInternet(value: string): boolean {
  const v = value.trim().toLowerCase()
  return isWildcardSource(v) || v === '0.0.0.0/0' || v === '::/0'
}

export interface SourceCheck {
  /** Error message, or undefined when the range is acceptable. */
  error?: string
}

/**
 * Admin source range rule (contract §3): valid CIDR; reject 0.0.0.0/0, ::/0,
 * anything wider than /16 and the strings "*" / "Internet" / "Any".
 */
export function checkAdminSourceCidr(value: string): SourceCheck {
  const v = value.trim()
  if (isWildcardSource(v)) return { error: `"${v}" is not allowed — restrict admin access to specific private ranges` }
  if (isOpenToInternet(v)) return { error: `${v} (the whole internet) is not allowed as an admin source` }
  const parsed = parseAnyCidr(v)
  if (!parsed) return { error: `"${v}" is not a valid CIDR (e.g. 10.0.0.0/16)` }
  if (parsed.family === 4 && !isAlignedCidr(parsed.cidr)) return { error: `${v} has host bits set — use the network address of the block` }
  if (parsed.prefix < 16) return { error: `${v} is wider than /16 — use a narrower range` }
  return {}
}

/** Ports that must never be reachable from the internet / any source. */
export const ADMIN_PORTS: readonly number[] = [22, 3389, 5985, 5986]

export interface PortRange { from: number; to: number }

/** "443" or "8000-8100" → range; null when invalid (1–65535, from ≤ to). */
export function parsePortRange(value: string): PortRange | null {
  const m = /^(\d{1,5})(?:-(\d{1,5}))?$/.exec(value.trim())
  if (!m) return null
  const from = Number(m[1])
  const to = m[2] === undefined ? from : Number(m[2])
  if (from < 1 || to > 65535 || from > to) return null
  return { from, to }
}

export function coversAdminPort(range: PortRange): boolean {
  return ADMIN_PORTS.some((p) => p >= range.from && p <= range.to)
}

/**
 * Inbound rule source rule (contract §3): a valid CIDR; never 0.0.0.0/0 / Internet
 * for admin ports 22/3389/5985/5986; and never wider than /8 unless a public IP
 * was explicitly requested.
 */
export function checkInboundSource(value: string, port: PortRange | null, publicIpRequired: boolean): SourceCheck {
  const v = value.trim()
  const adminPort = !!port && coversAdminPort(port)
  if (isOpenToInternet(v)) {
    if (adminPort) return { error: 'Admin ports (22, 3389, 5985, 5986) must never be open to the internet or any source' }
    if (isWildcardSource(v)) return { error: `"${v}" is not a CIDR — use a range such as 10.0.0.0/8` }
    if (!publicIpRequired) return { error: `${v} is only allowed when a public IP is required` }
    return {}
  }
  const parsed = parseAnyCidr(v)
  if (!parsed) return { error: `"${v}" is not a valid CIDR (e.g. 10.0.0.0/8)` }
  if (parsed.family === 4 && !isAlignedCidr(parsed.cidr)) return { error: `${v} has host bits set — use the network address of the block` }
  if (parsed.prefix < 8 && !publicIpRequired) return { error: `${v} is wider than /8 — only allowed when a public IP is required` }
  return {}
}
